import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  LinearProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  VpnKey,
  CheckCircle,
  Visibility,
  VisibilityOff,
  ErrorOutline,
} from '@mui/icons-material';
import api from '../../services/api';

const validatePassword = (password) => {
  const minLength = password.length >= 6;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  return {
    isValid: minLength && hasUpperCase && hasLowerCase && hasNumbers,
    requirements: { minLength, hasUpperCase, hasLowerCase, hasNumbers },
  };
};

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      validateToken(tokenFromUrl);
    } else {
      setValidating(false);
      setError('Invalid reset link. The link may be malformed or missing.');
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateToken = async (t) => {
    try {
      const response = await api.post('/auth/validate-reset-token', { token: t });
      if (response.data.valid) {
        setTokenValid(true);
      } else {
        setError('This reset link is invalid or has expired.');
      }
    } catch {
      setError('This reset link is invalid or has expired. Please request a new one.');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const { isValid } = validatePassword(newPassword);
    if (!isValid) {
      setError('Password does not meet the requirements below');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/reset-password', { token, newPassword });
      if (response.data.success) {
        setSuccess(response.data.message || 'Your password has been reset successfully.');
        setTimeout(() => navigate('/login'), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const pwValidation = validatePassword(newPassword);

  if (validating) {
    return (
      <Container component="main" maxWidth="sm">
        <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <LinearProgress sx={{ width: '100%', mb: 2 }} />
          <Typography variant="body1" color="textSecondary">
            Validating reset link…
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box display="flex" alignItems="center" gap={1} justifyContent="center" mb={3}>
            <VpnKey color="primary" sx={{ fontSize: 32 }} />
            <Typography component="h1" variant="h5" fontWeight={700}>
              Reset Password
            </Typography>
          </Box>

          {/* Invalid / expired token state */}
          {!tokenValid && error && (
            <Box textAlign="center" py={1}>
              <ErrorOutline color="error" sx={{ fontSize: 56, mb: 1 }} />
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                {error}
              </Alert>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Reset links expire after 1 hour. You can request a new one from the login page.
              </Typography>
              <Button
                variant="contained"
                component={Link}
                to="/login"
                size="large"
              >
                Back to Login
              </Button>
            </Box>
          )}

          {/* Valid token — show the form */}
          {tokenValid && !success && (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="New Password"
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                  error={Boolean(newPassword && !pwValidation.isValid)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowNew((v) => !v)} edge="end" tabIndex={-1}>
                          {showNew ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Confirm New Password"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  error={Boolean(confirmPassword && newPassword !== confirmPassword)}
                  helperText={
                    confirmPassword && newPassword !== confirmPassword
                      ? 'Passwords do not match'
                      : ''
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirm((v) => !v)} edge="end" tabIndex={-1}>
                          {showConfirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Password requirements panel */}
                {newPassword && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      mt: 1,
                      mb: 1,
                      bgcolor: 'background.default',
                      borderColor: pwValidation.isValid ? 'success.main' : 'divider',
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Password Requirements:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      {[
                        [pwValidation.requirements.minLength, 'At least 6 characters'],
                        [pwValidation.requirements.hasLowerCase, 'One lowercase letter'],
                        [pwValidation.requirements.hasUpperCase, 'One uppercase letter'],
                        [pwValidation.requirements.hasNumbers, 'One number'],
                      ].map(([met, label]) => (
                        <Typography
                          key={label}
                          component="li"
                          variant="body2"
                          sx={{ color: met ? 'success.main' : 'error.main' }}
                        >
                          {label}
                        </Typography>
                      ))}
                    </Box>
                  </Paper>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2, mb: 1 }}
                  disabled={loading || !newPassword || !confirmPassword}
                  size="large"
                >
                  {loading ? 'Resetting Password…' : 'Reset Password'}
                </Button>

                {loading && <LinearProgress sx={{ mt: 1 }} />}
              </form>
            </>
          )}

          {/* Success state */}
          {success && (
            <Box textAlign="center" py={1}>
              <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" gutterBottom color="success.main">
                Password Reset Successful!
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                {success}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
                size="large"
                fullWidth
              >
                Go to Login
              </Button>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Redirecting automatically in a few seconds…
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
}

export default ResetPassword;
